/**
 * CoachingBookSessionModal
 * Two-panel booking modal for coaching sessions (session details + date/time picker)
 * Transitions to BookingConfirmation on success
 */

import { XIcon } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog"
import { useRescheduleBooking } from "@/features/booking/hooks/useBooking"
import { BookingConfirmation } from "../../booking/components/BookingConfirmation"
import { useBookCoachingSession } from "../hooks/useCoachingBooking"
import { CoachingDateTimeSelector } from "./CoachingDateTimeSelector"
import { CoachingSessionInfoPanel } from "./CoachingSessionInfoPanel"
import { RequestTimeModal } from "./RequestTimeModal"

interface CoachingBookSessionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  session: {
    id: string
    coverImage: string | null
    title: string
    description?: string
    format: "IN_PERSON" | "VIRTUAL" | "HYBRID"
    duration: number
    durationType: "minutes" | "hours" | "days"
    price: number | null
    participationFee: "Free" | "Paid"
    websiteLink: string | null
    streetAddress?: string | null
    tags: string[]
  }
  expert: {
    id: string
    name: string | null
    username: string | null
    profilePictureUrl: string | null
  }
  bookingId?: string
}

export function CoachingBookSessionModal({
  open,
  onOpenChange,
  session,
  expert,
  bookingId,
}: CoachingBookSessionModalProps) {
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showRequestTime, setShowRequestTime] = useState(false)
  const [bookingResult, setBookingResult] = useState<{
    id: string
    date: string
    startTime: string
    endTime: string
  } | null>(null)

  const isReschedule = !!bookingId
  const bookSession = useBookCoachingSession()
  const rescheduleSession = useRescheduleBooking()

  // Reset internal state when parent closes the modal
  useEffect(() => {
    if (!open) {
      setShowConfirmation(false)
      setShowRequestTime(false)
      setBookingResult(null)
    }
  }, [open])

  function handleBook(availabilityId: string, message: string) {
    if (isReschedule && bookingId) {
      rescheduleSession.mutate(
        { bookingId, availabilityId },
        {
          onSuccess: () => {
            toast.success("Session rescheduled successfully")
            handleClose()
          },
          onError: (err: Error) => {
            toast.error(err.message || "Failed to reschedule session")
          },
        },
      )
      return
    }

    const baseUrl = window.location.origin
    const successUrl = `${baseUrl}/my-rituality?tab=sessions&subTab=booked&payment=success`
    const cancelUrl = `${baseUrl}/my-rituality?tab=sessions&subTab=booked&payment=cancelled`

    // Store booking details in localStorage for confirmation modal after Stripe redirect
    const bookingDetails = {
      id: `pending-${session.id}`, // Temporary ID, will be replaced after payment
      ritualTitle: session.title,
      date: new Date().toISOString(), // Will be updated with actual booking date
      startTime: "",
      endTime: "",
      duration: durationString,
      location:
        session.format === "IN_PERSON"
          ? "In Person"
          : session.format === "VIRTUAL"
            ? "Virtual"
            : "Hybrid",
      sessionType: "Private session",
      coachName: expert.name,
      websiteUrl: session.websiteLink ?? undefined,
      fee: session.price,
      feeCurrency: "USD",
      expertId: expert.id, // Store expert ID for redirect after confirmation
    }
    localStorage.setItem("pendingBookingConfirmation", JSON.stringify(bookingDetails))

    bookSession.mutate(
      { coachingSessionId: session.id, availabilityId, message, successUrl, cancelUrl },
      {
        onSuccess: data => {
          if (data.checkoutUrl) {
            // Store the actual booking details that will be available after payment
            const confirmedBooking = {
              ...bookingDetails,
              id: data.booking.id,
              date: data.booking.date,
              startTime: data.booking.startTime,
              endTime: data.booking.endTime,
            }
            localStorage.setItem("confirmedBookingDetails", JSON.stringify(confirmedBooking))
            window.location.href = data.checkoutUrl
            return
          }
          setBookingResult({
            id: data.booking.id,
            date: data.booking.date,
            startTime: data.booking.startTime,
            endTime: data.booking.endTime,
          })
          setShowConfirmation(true)
        },
        onError: (err: Error) => {
          localStorage.removeItem("pendingBookingConfirmation")
          localStorage.removeItem("confirmedBookingDetails")
          toast.error(err.message || "Failed to book session")
        },
      },
    )
  }

  function handleRequestTime() {
    setShowRequestTime(true)
  }

  function handleClose() {
    setShowConfirmation(false)
    setBookingResult(null)
    onOpenChange(false)
  }

  // Format the session data for the info panel
  const durationString =
    session.durationType === "minutes"
      ? `${session.duration} min`
      : session.durationType === "hours"
        ? `${session.duration} hour${session.duration > 1 ? "s" : ""}`
        : `${session.duration} day${session.duration > 1 ? "s" : ""}`

  // Show confirmation dialog (tied to parent's open state)
  if (open && showConfirmation && bookingResult) {
    return (
      <BookingConfirmation
        open
        onClose={handleClose}
        booking={{
          id: bookingResult.id,
          ritualTitle: session.title,
          date: bookingResult.date,
          startTime: bookingResult.startTime,
          endTime: bookingResult.endTime,
          duration: durationString,
          location:
            session.format === "IN_PERSON"
              ? "In Person"
              : session.format === "VIRTUAL"
                ? "Virtual"
                : "Hybrid",
          sessionType: "Private session",
          coachName: expert.name,
          websiteUrl: session.websiteLink ?? undefined,
          fee: session.price,
          feeCurrency: "USD",
        }}
      />
    )
  }

  // Two-panel booking modal
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="!max-w-[1080px] max-h-[90vh] overflow-y-auto border-connect-glass-border bg-[#1B1B1B] p-0 text-connect-cream sm:rounded-[30px]"
          overlayClass="bg-[rgba(2,2,2,0.9)] backdrop-blur-[25px]"
          showCloseButton={false}
        >
          <DialogTitle className="sr-only">
            {isReschedule ? "Reschedule" : "Book a Session"}: {session.title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isReschedule
              ? "Select a new date and time to reschedule your session"
              : "Select a date and time to book a coaching session"}
          </DialogDescription>

          <div className="flex flex-col lg:flex-row">
            {/* Left Panel - Coaching Session Info */}
            <div className="relative border-b border-[#F2F0EB26] lg:border-b-0 lg:border-r">
              <DialogClose className="absolute right-4 top-4 z-10 flex size-[44px] cursor-pointer items-center justify-center rounded-full border border-[#F2F0EB26] bg-[#F2F0EB0D] text-[#F2F0EB] transition-opacity hover:opacity-80 lg:hidden">
                <XIcon className="size-5 text-connect-cream" />
                <span className="sr-only">Close</span>
              </DialogClose>
              <CoachingSessionInfoPanel
                coverImage={session.coverImage}
                title={session.title}
                description={session.description}
                format={session.format}
                duration={session.duration}
                durationType={session.durationType}
                coachName={expert.name}
                coachUsername={expert.username}
                coachAvatar={expert.profilePictureUrl}
                price={session.price}
                participationFee={session.participationFee}
                websiteLink={session.websiteLink}
                location={session.streetAddress ?? null}
              />
            </div>

            {/* Right Panel - Date/Time Selector */}
            <div className="relative flex-1 overflow-y-auto bg-[#252525]">
              <DialogClose className="absolute right-4 top-4 z-10 flex size-[44px] cursor-pointer items-center justify-center rounded-full border border-[#F2F0EB26] bg-[#F2F0EB0D] text-[#F2F0EB] transition-opacity hover:opacity-80 max-lg:hidden">
                <XIcon className="size-5 text-connect-cream" />
                <span className="sr-only">Close</span>
              </DialogClose>
              <CoachingDateTimeSelector
                coachingSessionId={session.id}
                expertName={expert.name}
                onBook={handleBook}
                isBooking={isReschedule ? rescheduleSession.isPending : bookSession.isPending}
                onRequestTime={handleRequestTime}
                heading={isReschedule ? "Select New Date & Time" : undefined}
                actionLabel={isReschedule ? "Reschedule Session" : undefined}
                actioningLabel={isReschedule ? "Rescheduling..." : undefined}
                isReschedule={isReschedule}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Time Modal */}
      <RequestTimeModal
        open={showRequestTime}
        onOpenChange={setShowRequestTime}
        coachingSessionId={session.id}
        sessionTitle={session.title}
        expertName={expert.name}
      />
    </>
  )
}
